import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import Cookies from "js-cookie";

// Types
export interface UserData {
  userName: string;
  phoneNumber: string | null;
  email: string | null;
  emailConfirmed: boolean;
}

export interface AuthTokens {
  token: string;
  refreshToken: string;
  expiration: string | null;
}

export interface UserState {
  userDetails: UserData | null;
  token: string | null;
  refreshToken: string | null;
  tokenExpiry: string | null;
  status: number | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Helper function to check token expiration
const isTokenExpired = (tokenExpiry: string | null): boolean => {
  if (!tokenExpiry) return true;

  try {
    const expiryDate = new Date(tokenExpiry);
    const now = new Date();
    // Add 5 minute buffer for proactive refresh
    const bufferTime = 5 * 60 * 1000;
    return now.getTime() >= expiryDate.getTime() - bufferTime;
  } catch {
    return true;
  }
};

// Get initial state
const getInitialState = (): UserState => {
  const storedUserDetails = localStorage.getItem("userDetails");
  const userDetails = storedUserDetails ? JSON.parse(storedUserDetails) : null;

  const token = Cookies.get("access_token") || null;
  const refreshToken = Cookies.get("refresh_token") || null;
  const tokenExpiry = Cookies.get("token_expiry") || null;

  const tokenValid = token && !isTokenExpired(tokenExpiry);

  return {
    userDetails,
    token,
    refreshToken,
    tokenExpiry,
    status: null,
    isAuthenticated: !!(tokenValid && userDetails),
    isLoading: false,
    error: null,
  };
};

// Create slice
const userSlice = createSlice({
  name: "user",
  initialState: getInitialState(),
  reducers: {
    // Login success
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },

    loginSuccess: (
      state,
      action: PayloadAction<{
        userDetails: UserData;
        token: string;
        refreshToken: string;
        expiration: string | null;
        status: number;
      }>
    ) => {
      state.userDetails = action.payload.userDetails;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.tokenExpiry = action.payload.expiration;
      state.status = action.payload.status;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;

      // Store in cookies
      const tokenExpiryDate = action.payload.expiration
        ? new Date(action.payload.expiration)
        : new Date(Date.now() + 24 * 60 * 60 * 1000);

      Cookies.set("access_token", action.payload.token, {
        expires: tokenExpiryDate,
        secure: process.env.NODE_ENV === "production",
        sameSite: "strict",
        path: "/",
      });

      const refreshTokenExpiry = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
      Cookies.set("refresh_token", action.payload.refreshToken, {
        expires: refreshTokenExpiry,
        secure: process.env.NODE_ENV === "production",
        sameSite: "strict",
        path: "/",
      });

      if (action.payload.expiration) {
        Cookies.set("token_expiry", action.payload.expiration, {
          expires: tokenExpiryDate,
          secure: process.env.NODE_ENV === "production",
          sameSite: "strict",
          path: "/",
        });
      }

      localStorage.setItem(
        "userDetails",
        JSON.stringify(action.payload.userDetails)
      );
    },

    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
    },

    // Token refresh
    refreshTokensStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },

    refreshTokensSuccess: (
      state,
      action: PayloadAction<{
        token: string;
        refreshToken: string;
        expiration: string | null;
        userDetails: UserData;
      }>
    ) => {
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.tokenExpiry = action.payload.expiration;
      state.userDetails = action.payload.userDetails;
      state.isLoading = false;
      state.isAuthenticated = true;
      state.error = null;

      // Update cookies
      const tokenExpiryDate = action.payload.expiration
        ? new Date(action.payload.expiration)
        : new Date(Date.now() + 24 * 60 * 60 * 1000);

      Cookies.set("access_token", action.payload.token, {
        expires: tokenExpiryDate,
        secure: process.env.NODE_ENV === "production",
        sameSite: "strict",
        path: "/",
      });

      Cookies.set("refresh_token", action.payload.refreshToken, {
        expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        secure: process.env.NODE_ENV === "production",
        sameSite: "strict",
        path: "/",
      });

      if (action.payload.expiration) {
        Cookies.set("token_expiry", action.payload.expiration, {
          expires: tokenExpiryDate,
          secure: process.env.NODE_ENV === "production",
          sameSite: "strict",
          path: "/",
        });
      }

      localStorage.setItem(
        "userDetails",
        JSON.stringify(action.payload.userDetails)
      );
    },

    refreshTokensFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
      state.isAuthenticated = false;

      Cookies.remove("access_token");
      Cookies.remove("refresh_token");
      Cookies.remove("token_expiry");
    },

    // Logout
    logout: (state) => {
      state.userDetails = null;
      state.token = null;
      state.refreshToken = null;
      state.tokenExpiry = null;
      state.status = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;

      Cookies.remove("access_token");
      Cookies.remove("refresh_token");
      Cookies.remove("token_expiry");
      localStorage.removeItem("userDetails");
    },

    // Check authentication
    checkAuthentication: (state) => {
      const token = Cookies.get("access_token");
      const refreshToken = Cookies.get("refresh_token");
      const tokenExpiry: any = Cookies.get("token_expiry");
      const storedUserDetails = localStorage.getItem("userDetails");

      const userDetails = storedUserDetails
        ? JSON.parse(storedUserDetails)
        : null;
      const isValid = token && !isTokenExpired(tokenExpiry) && userDetails;

      state.token = token || null;
      state.refreshToken = refreshToken || null;
      state.tokenExpiry = tokenExpiry || null;
      state.userDetails = userDetails;
      state.isAuthenticated = !!isValid;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
});

// Export actions
export const {
  loginStart,
  loginSuccess,
  loginFailure,
  refreshTokensStart,
  refreshTokensSuccess,
  refreshTokensFailure,
  logout,
  checkAuthentication,
  clearError,
} = userSlice.actions;

// Selectors
export const selectUser = (state: { user: UserState }) =>
  state.user.userDetails;
export const selectToken = (state: { user: UserState }) => state.user.token;
export const selectRefreshToken = (state: { user: UserState }) =>
  state.user.refreshToken;
export const selectIsAuthenticated = (state: { user: UserState }) =>
  state.user.isAuthenticated;
export const selectIsLoading = (state: { user: UserState }) =>
  state.user.isLoading;
export const selectError = (state: { user: UserState }) => state.user.error;

export default userSlice.reducer;

// import { createSlice, PayloadAction } from "@reduxjs/toolkit";
// import Cookies from "js-cookie";

// // Define types for the state based on new API response
// interface UserState {
//   userDetails: {
//     userName: string | null;
//     phoneNumber: string | null;
//     email: string | null;
//     emailConfirmed: boolean;
//   } | null;
//   token: string | null;
//   refreshToken: string | null;
//   status: number | null;
//   isAuthenticated: boolean;
//   tokenExpiry: string | null;
// }

// // Get initial state from cookies and localStorage
// const getInitialState = (): UserState => {
//   // Try to get user details from localStorage (optional)
//   const storedUserDetails = localStorage.getItem("userDetails");
//   const userDetails = storedUserDetails ? JSON.parse(storedUserDetails) : null;

//   // Get tokens from cookies (primary storage)
//   const token = Cookies.get("access_token") || null;
//   const refreshToken = Cookies.get("refresh_token") || null;
//   const tokenExpiry = Cookies.get("token_expiry") || null;

//   return {
//     userDetails,
//     token,
//     refreshToken,
//     status: null, // Not stored persistently
//     isAuthenticated: !!(token && userDetails), // Check if both token and user details exist
//     tokenExpiry,
//   };
// };

// // Create initial state
// const initialState: UserState = getInitialState();

// // Create the slice
// const userSlice = createSlice({
//   name: "user",
//   initialState,
//   reducers: {
//     // Action to set user data from new API response
//     setUserDetails: (
//       state,
//       action: PayloadAction<{
//         userDetails: {
//           userName: string | null;
//           phoneNumber: string | null;
//           email: string | null;
//           emailConfirmed: boolean;
//         };
//         token: string;
//         refreshToken: string;
//         status: number;
//       }>
//     ) => {
//       state.userDetails = action.payload.userDetails;
//       state.token = action.payload.token;
//       state.refreshToken = action.payload.refreshToken;
//       state.status = action.payload.status;
//       state.isAuthenticated = true;

//       // Store tokens in cookies
//       Cookies.set("access_token", action.payload.token, {
//         expires: 1, // 1 day, adjust as needed
//         secure: process.env.NODE_ENV === "production",
//         sameSite: "strict",
//         path: "/",
//       });

//       Cookies.set("refresh_token", action.payload.refreshToken, {
//         expires: 7, // 7 days, adjust as needed
//         secure: process.env.NODE_ENV === "production",
//         sameSite: "strict",
//         path: "/",
//       });

//       // Store user details in localStorage (optional)
//       localStorage.setItem(
//         "userDetails",
//         JSON.stringify(action.payload.userDetails)
//       );
//     },

//     // Action to update tokens (for refresh token flow)
//     updateTokens: (
//       state,
//       action: PayloadAction<{
//         token: string;
//         refreshToken?: string;
//         tokenExpiry?: string;
//       }>
//     ) => {
//       state.token = action.payload.token;

//       if (action.payload.refreshToken) {
//         state.refreshToken = action.payload.refreshToken;
//         Cookies.set("refresh_token", action.payload.refreshToken, {
//           expires: 7,
//           secure: process.env.NODE_ENV === "production",
//           sameSite: "strict",
//           path: "/",
//         });
//       }

//       if (action.payload.tokenExpiry) {
//         state.tokenExpiry = action.payload.tokenExpiry;
//         Cookies.set("token_expiry", action.payload.tokenExpiry, {
//           expires: new Date(action.payload.tokenExpiry),
//           secure: process.env.NODE_ENV === "production",
//           sameSite: "strict",
//           path: "/",
//         });
//       }

//       // Update access token cookie
//       Cookies.set("access_token", action.payload.token, {
//         expires: action.payload.tokenExpiry
//           ? new Date(action.payload.tokenExpiry)
//           : 1,
//         secure: process.env.NODE_ENV === "production",
//         sameSite: "strict",
//         path: "/",
//       });
//     },

//     // Action to update user details
//     updateUserDetails: (
//       state,
//       action: PayloadAction<
//         Partial<{
//           userName: string | null;
//           phoneNumber: string | null;
//           email: string | null;
//           emailConfirmed: boolean;
//         }>
//       >
//     ) => {
//       if (state.userDetails) {
//         state.userDetails = { ...state.userDetails, ...action.payload };
//         localStorage.setItem("userDetails", JSON.stringify(state.userDetails));
//       }
//     },

//     // Action to clear user data (logout)
//     clearUserDetails: (state) => {
//       state.userDetails = null;
//       state.token = null;
//       state.refreshToken = null;
//       state.status = null;
//       state.isAuthenticated = false;
//       state.tokenExpiry = null;

//       // Clear cookies
//       Cookies.remove("access_token");
//       Cookies.remove("refresh_token");
//       Cookies.remove("token_expiry");

//       // Clear localStorage
//       localStorage.removeItem("userDetails");
//     },

//     // Action to set authentication status
//     setAuthenticationStatus: (state, action: PayloadAction<boolean>) => {
//       state.isAuthenticated = action.payload;
//     },

//     // Action to check and update token status
//     checkTokenValidity: (state) => {
//       const token = Cookies.get("access_token");
//       const userDetails = localStorage.getItem("userDetails");

//       state.token = token || null;
//       state.userDetails = userDetails ? JSON.parse(userDetails) : null;
//       state.isAuthenticated = !!(token && userDetails);
//     },
//   },
// });

// // Export the actions
// export const {
//   setUserDetails,
//   clearUserDetails,
//   updateTokens,
//   updateUserDetails,
//   setAuthenticationStatus,
//   checkTokenValidity,
// } = userSlice.actions;

// // Selectors for easy access to state
// export const selectUser = (state: { user: UserState }) =>
//   state.user.userDetails;
// export const selectToken = (state: { user: UserState }) => state.user.token;
// export const selectRefreshToken = (state: { user: UserState }) =>
//   state.user.refreshToken;
// export const selectIsAuthenticated = (state: { user: UserState }) =>
//   state.user.isAuthenticated;
// export const selectUserStatus = (state: { user: UserState }) =>
//   state.user.status;
// export const selectTokenExpiry = (state: { user: UserState }) =>
//   state.user.tokenExpiry;

// // Helper function to check if token is expired (can be used in middleware or components)
// export const isTokenExpired = (): boolean => {
//   const tokenExpiry = Cookies.get("token_expiry");
//   if (!tokenExpiry) return true;

//   const expiryDate = new Date(tokenExpiry);
//   const now = new Date();
//   return now >= expiryDate;
// };

// // Export the reducer
// export default userSlice.reducer;

// // import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// // // Define types for the state
// // interface UserState {
// //   userDetails: any | null;
// //   permissions: any | null;
// //   token: string | null;
// //   status: any;
// // }

// // // Define initial state, loading from localStorage if available
// // const initialState: UserState = {
// //   userDetails: JSON.parse(localStorage.getItem('userDetails') || 'null'),
// //   permissions: JSON.parse(localStorage.getItem('permissions') || 'null') || [], // Make sure it's an empty array if no permissions are found
// //   token: localStorage.getItem('token') || null,
// //   status: JSON.parse(localStorage.getItem('status') || 'null'),
// // };

// // // Create the slice
// // const userSlice = createSlice({
// //   name: 'user',
// //   initialState,
// //   reducers: {
// //     // Action to set user data
// //     setUserDetails: (state, action: PayloadAction<{ userDetails: any, permissions: any, token: string, status: any }>) => {
// //       state.userDetails = action.payload.userDetails;
// //       state.permissions = action.payload.permissions;
// //       state.token = action.payload.token;
// //       state.status = action.payload.status;

// //       // Save to localStorage for persistence
// //       localStorage.setItem('userDetails', JSON.stringify(action.payload.userDetails));
// //       localStorage.setItem('permissions', JSON.stringify(action.payload.permissions));
// //       localStorage.setItem('token', action.payload.token);
// //       localStorage.setItem('status', JSON.stringify(action.payload.status));
// //     },
// //     // Action to clear user data
// //     clearUserDetails: (state) => {
// //       state.userDetails = null;
// //       state.permissions = [];
// //       state.token = null;
// //       state.status = null;

// //       // Clear from localStorage as well
// //       localStorage.removeItem('userDetails');
// //       localStorage.removeItem('permissions');
// //       localStorage.removeItem('token');
// //       localStorage.removeItem('status');
// //     },
// //   },
// // });

// // // Export the actions
// // export const { setUserDetails, clearUserDetails } = userSlice.actions;

// // // Export the reducer
// // export default userSlice.reducer;
